'use client';

import React, { useState, useRef, useEffect } from 'react';
import { X, MapPin, User, Mail, Phone, Briefcase, Send } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { gemeindeListe } from '@/data/gemeindeListe';
import styles from '@/styles/analyse-anfrage-modal.module.css';
import { trackModalEvent, trackFormEvent } from '@/utils/umami-tracking';

interface Gemeinde {
  nr: string;
  name: string;
  plz: string;
}

interface AnalyseAnfrageModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  gemeinde: string;
  gemeindeId: string;
  name: string;
  email: string;
  phone: string;
  rolle: string;
  // Optional fields
  verbrauchsdaten2024: string;
  einspeisedaten2024: string;
  anzahlGemeindegebaeude: string;
  hatGeschlosseneEEG: string;
}

export function AnalyseAnfrageModal({ isOpen, onClose }: AnalyseAnfrageModalProps) {
  const [formData, setFormData] = useState<FormData>({
    gemeinde: '',
    gemeindeId: '',
    name: '',
    email: '',
    phone: '',
    rolle: '',
    // Optional fields
    verbrauchsdaten2024: '',
    einspeisedaten2024: '',
    anzahlGemeindegebaeude: '',
    hatGeschlosseneEEG: ''
  });
  
  const [gemeindeQuery, setGemeindeQuery] = useState('');
  const [gemeindeResults, setGemeindeResults] = useState<Gemeinde[]>([]);
  const [showGemeindeDropdown, setShowGemeindeDropdown] = useState(false);
  const [errors, setErrors] = useState<Partial<FormData>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [showOptionalForm, setShowOptionalForm] = useState(false);
  const [formStarted, setFormStarted] = useState(false);
  const formStartTime = useRef<number>(0);
  
  const gemeindeInputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  // Gemeinde search effect
  useEffect(() => {
    if (!gemeindeQuery) {
      setGemeindeResults([]);
      setShowGemeindeDropdown(false);
      return;
    }
    
    // Don't show dropdown if exact match is selected
    if (formData.gemeinde && gemeindeQuery === formData.gemeinde) {
      setGemeindeResults([]);
      setShowGemeindeDropdown(false);
      return;
    }
    
    const q = gemeindeQuery.toLowerCase();
    const filtered = gemeindeListe
      .filter(g =>
        g.name.toLowerCase().includes(q) ||
        g.plz.startsWith(q)
      )
      .sort((a, b) => a.name.localeCompare(b.name))
      .slice(0, 6);
    
    setGemeindeResults(filtered);
    setShowGemeindeDropdown(filtered.length > 0);
  }, [gemeindeQuery, formData.gemeinde]);

  // Handle gemeinde selection
  const handleGemeindeSelect = (gemeinde: Gemeinde) => {
    setFormData(prev => ({
      ...prev,
      gemeinde: gemeinde.name,
      gemeindeId: gemeinde.nr
    }));
    setGemeindeQuery(gemeinde.name);
    setGemeindeResults([]);
    setShowGemeindeDropdown(false);
    setErrors(prev => ({ ...prev, gemeinde: undefined }));
    
    // Blur the input to prevent refocus issues
    if (gemeindeInputRef.current) {
      gemeindeInputRef.current.blur();
    }
  };

  // Form validation
  const validateForm = (): boolean => {
    const newErrors: Partial<FormData> = {};
    
    if (!formData.gemeinde) newErrors.gemeinde = 'Bitte wählen Sie eine Gemeinde';
    if (!formData.name.trim()) newErrors.name = 'Name ist erforderlich';
    if (!formData.email.trim()) {
      newErrors.email = 'E-Mail ist erforderlich';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Ungültige E-Mail-Adresse';
    }
    if (!formData.phone.trim()) newErrors.phone = 'Telefonnummer ist erforderlich';
    if (!formData.rolle.trim()) newErrors.rolle = 'Rolle ist erforderlich';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      // Prepare basic payload for webhook (first submission)
      const payload = {
        gemeinde: formData.gemeinde,
        gemeindeId: formData.gemeindeId,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        rolle: formData.rolle,
        timestamp: new Date().toISOString(),
        source: 'public-landing-page',
        step: 'basic_info'
      };

      // Send to webhook
      const webhookUrl = process.env.NEXT_PUBLIC_REQUEST_WEBHOOK_URL;
      if (webhookUrl) {
        const response = await fetch(webhookUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
          // Minimize headers to avoid "Request Header Fields Too Large"
          credentials: 'omit'
        });
        
        if (!response.ok) {
          console.warn(`Webhook response: ${response.status} ${response.statusText}`);
        }
      }
      
      // Track form submission
      trackFormEvent('submit', {
        step: 'basic_info',
        gemeinde_id: formData.gemeindeId,
        rolle: formData.rolle
      });
      
      setSubmitSuccess(true);
      
      // Show optional form after 2 seconds
      setTimeout(() => {
        setSubmitSuccess(false);
        setShowOptionalForm(true);
      }, 2000);
      
    } catch (error) {
      console.error('Fehler beim Senden der Anfrage:', error);
      // Still show success to user - error handling can be done server-side
      setSubmitSuccess(true);
      setTimeout(() => {
        setSubmitSuccess(false);
        setShowOptionalForm(true);
      }, 2000);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle optional data submission
  const handleOptionalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Prepare complete data payload (second submission with all data)
      const completePayload = {
        gemeinde: formData.gemeinde,
        gemeindeId: formData.gemeindeId,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        rolle: formData.rolle,
        // Additional optional data
        verbrauchsdaten2024: formData.verbrauchsdaten2024 || null,
        einspeisedaten2024: formData.einspeisedaten2024 || null,
        anzahlGemeindegebaeude: formData.anzahlGemeindegebaeude || null,
        hatGeschlosseneEEG: formData.hatGeschlosseneEEG || null,
        timestamp: new Date().toISOString(),
        source: 'public-landing-page',
        step: 'complete_info'
      };

      // Send optional data to webhook
      const webhookUrl = process.env.NEXT_PUBLIC_REQUEST_WEBHOOK_URL;
      if (webhookUrl) {
        const response = await fetch(webhookUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(completePayload),
          // Minimize headers to avoid "Request Header Fields Too Large"
          credentials: 'omit'
        });
        
        if (!response.ok) {
          console.warn(`Complete webhook response: ${response.status} ${response.statusText}`);
        }
      }
      
      // Track optional form completion
      trackFormEvent('complete', {
        step: 'optional_info',
        gemeinde_id: formData.gemeindeId
      });
      
      // Close modal after optional submission
      setTimeout(() => {
        handleModalClose('form_complete');
      }, 1000);
      
    } catch (error) {
      console.error('Fehler beim Senden der optionalen Daten:', error);
      // Close modal anyway
      setTimeout(() => {
        onClose();
        setFormData({
          gemeinde: '',
          gemeindeId: '',
          name: '',
          email: '',
          phone: '',
          rolle: '',
          verbrauchsdaten2024: '',
          einspeisedaten2024: '',
          anzahlGemeindegebaeude: '',
          hatGeschlosseneEEG: ''
        });
        setGemeindeQuery('');
        setShowOptionalForm(false);
        setErrors({});
      }, 1000);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle input changes
  const handleInputChange = (field: keyof FormData, value: string) => {
    // Track form start on first interaction
    if (!formStarted) {
      trackFormEvent('start', { first_field: field });
      setFormStarted(true);
    }
    
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  // Handle modal close with tracking
  const handleModalClose = (reason: string = 'user_close') => {
    let formProgress = 'not_started';
    if (submitSuccess && showOptionalForm) {
      formProgress = 'full_complete';
    } else if (submitSuccess) {
      formProgress = 'basic_complete';
    } else if (formStarted) {
      formProgress = 'partial';
      // Track abandonment
      const fieldsCompleted = Object.values(formData).filter(v => v !== '').length;
      const timeInForm = Date.now() - formStartTime.current;
      trackFormEvent('abandon', {
        fields_completed_count: fieldsCompleted,
        time_in_form_ms: timeInForm
      });
    }
    
    trackModalEvent('close', { reason, form_progress: formProgress });
    onClose();
    
    // Reset form state
    setTimeout(() => {
      setFormData({
        gemeinde: '',
        gemeindeId: '',
        name: '',
        email: '',
        phone: '',
        rolle: '',
        verbrauchsdaten2024: '',
        einspeisedaten2024: '',
        anzahlGemeindegebaeude: '',
        hatGeschlosseneEEG: ''
      });
      setGemeindeQuery('');
      setShowOptionalForm(false);
      setSubmitSuccess(false);
      setFormStarted(false);
      setErrors({});
    }, 300);
  };

  // Close modal on ESC key and track modal events
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleModalClose('user_close');
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
      trackModalEvent('open', { trigger: 'cta' });
      formStartTime.current = Date.now();
    }
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (gemeindeInputRef.current && !gemeindeInputRef.current.closest('.field-group')?.contains(e.target as Node)) {
        setShowGemeindeDropdown(false);
      }
    };

    if (showGemeindeDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showGemeindeDropdown]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className={styles.modalOverlay}
        onClick={(e) => e.target === e.currentTarget && handleModalClose('backdrop_click')}
      >
        <motion.div
          ref={modalRef}
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ type: "spring", damping: 20, stiffness: 300 }}
          className={styles.modalContainer}
        >
          {/* Close Button */}
          <button
            onClick={() => handleModalClose('user_close')}
            className={styles.closeButton}
            aria-label="Schließen"
          >
            <X style={{ width: '1.25rem', height: '1.25rem', color: '#4b5563' }} />
          </button>

          <div className={styles.modalContent}>
            {/* Header */}
            <div className={styles.header}>
              <h2 className={styles.title}>
                Unverbindliche Potenzialanalyse
              </h2>
              <p className={styles.subtitle}>
                Erfahrt in wenigen Tagen, welches Einsparpotenzial eure Gemeinde hat
              </p>
            </div>

            {submitSuccess ? (
              /* Success State */
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className={styles.successState}
              >
                <div className={styles.successIcon}>
                  <Send className={styles.successIconSvg} />
                </div>
                <h3 className={styles.successTitle}>
                  Anfrage gesendet!
                </h3>
                <p className={styles.successMessage}>
                  Wir melden uns innerhalb von 24 Stunden bei euch.
                </p>
              </motion.div>
            ) : showOptionalForm ? (
              /* Optional Form */
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className={styles.optionalForm}
              >
                <div className={styles.optionalHeader}>
                  <h3 className={styles.optionalTitle}>
                    Noch ein paar Details?
                  </h3>
                  <p className={styles.optionalMessage}>
                    Diese Angaben helfen uns, eine präzisere Analyse zu erstellen
                  </p>
                </div>

                <form onSubmit={handleOptionalSubmit} className={styles.form}>
                  {/* Verbrauchsdaten 2024 */}
                  <div className={styles.fieldGroup}>
                    <label className={styles.label}>
                      Stromverbrauch 2024 (kWh)
                    </label>
                    <input
                      type="text"
                      value={formData.verbrauchsdaten2024}
                      onChange={(e) => handleInputChange('verbrauchsdaten2024', e.target.value)}
                      placeholder="z.B. 120000 kWh"
                      className={styles.input}
                    />
                  </div>

                  {/* Einspeisedaten 2024 */}
                  <div className={styles.fieldGroup}>
                    <label className={styles.label}>
                      Stromeinspeisung 2024 (kWh)
                    </label>
                    <input
                      type="text"
                      value={formData.einspeisedaten2024}
                      onChange={(e) => handleInputChange('einspeisedaten2024', e.target.value)}
                      placeholder="z.B. 50000 kWh"
                      className={styles.input}
                    />
                  </div>

                  <div className={styles.gridTwoColumns}>
                    {/* Anzahl Gemeindegebäude */}
                    <div className={styles.fieldGroup}>
                      <label className={styles.label}>
                        Anzahl Gemeindegebäude
                      </label>
                      <input
                        type="number"
                        value={formData.anzahlGemeindegebaeude}
                        onChange={(e) => handleInputChange('anzahlGemeindegebaeude', e.target.value)}
                        placeholder="z.B. 8"
                        className={styles.input}
                      />
                    </div>

                    {/* Geschlossene EEG vorhanden */}
                    <div className={styles.fieldGroup}>
                      <label className={styles.label}>
                        Geschlossene EEG vorhanden?
                      </label>
                      <select
                        value={formData.hatGeschlosseneEEG}
                        onChange={(e) => handleInputChange('hatGeschlosseneEEG', e.target.value)}
                        className={styles.select}
                      >
                        <option value="">Bitte wählen...</option>
                        <option value="ja">Ja, bereits vorhanden</option>
                        <option value="nein">Nein, noch keine EEG</option>
                        <option value="geplant">In Planung</option>
                      </select>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className={styles.actionButtons}>
                    <button
                      type="button"
                      onClick={() => handleModalClose('user_close')}
                      className={styles.secondaryButton}
                    >
                      Später
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className={styles.primaryButton}
                    >
                      {isSubmitting ? (
                        <>
                          <div className={styles.spinner}></div>
                          Senden...
                        </>
                      ) : (
                        <>
                          <Send className={styles.primaryButtonIcon} />
                          Daten ergänzen
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </motion.div>
            ) : (
              /* Form */
              <form onSubmit={handleSubmit} className={styles.form}>
                {/* Gemeinde Selection */}
                <div className={`${styles.fieldGroup} field-group`}>
                  <label className={styles.label}>
                    <MapPin className={styles.labelIcon} />
                    Gemeinde *
                  </label>
                  <input
                    ref={gemeindeInputRef}
                    type="text"
                    value={gemeindeQuery}
                    onChange={(e) => setGemeindeQuery(e.target.value)}
                    onFocus={() => gemeindeQuery && setShowGemeindeDropdown(true)}
                    placeholder="Gemeinde oder PLZ suchen..."
                    className={`${styles.input} ${errors.gemeinde ? styles.inputError : ''}`}
                  />
                  
                  {/* Gemeinde Dropdown */}
                  {showGemeindeDropdown && gemeindeResults.length > 0 && (
                    <div className={styles.dropdown}>
                      {gemeindeResults.map((gemeinde) => (
                        <button
                          key={gemeinde.nr}
                          type="button"
                          onClick={() => handleGemeindeSelect(gemeinde)}
                          className={styles.dropdownItem}
                        >
                          <div className={styles.dropdownItemName}>{gemeinde.name}</div>
                          <div className={styles.dropdownItemPlz}>PLZ: {gemeinde.plz}</div>
                        </button>
                      ))}
                    </div>
                  )}
                  
                  {errors.gemeinde && (
                    <p className={styles.errorMessage}>{errors.gemeinde}</p>
                  )}
                </div>

                {/* Name */}
                <div className={styles.fieldGroup}>
                  <label className={styles.label}>
                    <User className={styles.labelIcon} />
                    Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Ihr vollständiger Name"
                    className={`${styles.input} ${errors.name ? styles.inputError : ''}`}
                  />
                  {errors.name && (
                    <p className={styles.errorMessage}>{errors.name}</p>
                  )}
                </div>

                {/* Email */}
                <div className={styles.fieldGroup}>
                  <label className={styles.label}>
                    <Mail className={styles.labelIcon} />
                    E-Mail *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="ihre.email@gemeinde.at"
                    className={`${styles.input} ${errors.email ? styles.inputError : ''}`}
                  />
                  {errors.email && (
                    <p className={styles.errorMessage}>{errors.email}</p>
                  )}
                </div>

                {/* Phone */}
                <div className={styles.fieldGroup}>
                  <label className={styles.label}>
                    <Phone className={styles.labelIcon} />
                    Telefon *
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    placeholder="0512 40 50 60 70"
                    className={`${styles.input} ${errors.phone ? styles.inputError : ''}`}
                  />
                  {errors.phone && (
                    <p className={styles.errorMessage}>{errors.phone}</p>
                  )}
                </div>

                {/* Rolle */}
                <div className={styles.fieldGroup}>
                  <label className={styles.label}>
                    <Briefcase className={styles.labelIcon} />
                    Rolle in der Gemeinde *
                  </label>
                  <select
                    value={formData.rolle}
                    onChange={(e) => handleInputChange('rolle', e.target.value)}
                    className={`${styles.select} ${errors.rolle ? styles.inputError : ''}`}
                  >
                    <option value="">Bitte wählen...</option>
                    <option value="buergermeister">Bürgermeister/in</option>
                    <option value="vizebuergermeister">Vizebürgermeister/in</option>
                    <option value="gemeinderat">Gemeinderat/rätin</option>
                    <option value="amtsleiter">Amtsleiter/in</option>
                    <option value="sachbearbeiter">Sachbearbeiter/in</option>
                    <option value="umweltreferent">Umweltreferent/in</option>
                    <option value="energiereferent">Energiereferent/in</option>
                    <option value="andere">Andere Position</option>
                  </select>
                  {errors.rolle && (
                    <p className={styles.errorMessage}>{errors.rolle}</p>
                  )}
                </div>


                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={styles.submitButton}
                >
                  {isSubmitting ? (
                    <>
                      <div className={styles.spinner}></div>
                      Wird gesendet...
                    </>
                  ) : (
                    <>
                      <Send className={styles.submitButtonIcon} />
                      Unverbindliche Anfrage senden
                    </>
                  )}
                </button>

                <p className={styles.bottomText}>
                  Kostenlos und unverbindlich. Wir melden uns innerhalb von 24 Stunden.
                </p>
              </form>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}